import React, { useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { calculaImpostosIbsCbs } from '~/components/NovosInputs/InputIbsCbs/functions/calculaImpostosIbsCbs';

import { useEntradaNFE } from '~/pages/EntradaNFE/EntradaNFEContext';
import {
  fltRound,
  formatCurrencyAsText,
  roundByRule,
  toNumberOrZero,
} from '~/utils/functions';

type Calculos = {
  valTotProdItem: number;
  valBcIcms: number;
  valTotCredIcmsItem: number;
  valTotFrete: number;
  valTotRetidoItem: number;
  valTotStXml: number;
  valTotFcpSt: number;
  valTotIva: number;
  valTotIpiItem: number;
  valTotIcmsAntec: number;
  valTotNfItem: number;
  valTotNfItemPed: number;

  valTotBcIbsCbs: number;
  valTotCbs: number;
  valTotIbsEst: number;
  valTotIbsMun: number;
};

const INITIAL_CALCULOS: Calculos = {
  valTotProdItem: 0,
  valBcIcms: 0,
  valTotCredIcmsItem: 0,
  valTotFrete: 0,
  valTotRetidoItem: 0,
  valTotStXml: 0,
  valTotFcpSt: 0,
  valTotIva: 0,
  valTotIpiItem: 0,
  valTotIcmsAntec: 0,
  valTotNfItem: 0,
  valTotNfItemPed: 0,
  valTotBcIbsCbs: 0,
  valTotCbs: 0,
  valTotIbsEst: 0,
  valTotIbsMun: 0,
};

function getValue(obj: Record<string, any>, key: string) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (key.endsWith('_xml')) return obj[key];

  const base = obj[key];
  const xml = obj[`${key}_xml`];

  const valid = (v: any) => {
    if (v === null || v === undefined) return false;

    if (typeof v === 'number') return Number.isFinite(v) && v > 0;

    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return false;

      if (/^-?\d+(?:[.,]\d+)?$/.test(s)) {
        const n = Number(s.replace(',', '.'));
        return Number.isFinite(n) && n > 0;
      }
      return true;
    }

    return true;
  };

  if (key in obj) return valid(base) ? base : `${key}_xml` in obj ? xml : base;
  return `${key}_xml` in obj ? xml : undefined;
}

/**
 * Componente de resumo dos valores totais da NF,
 * incluindo produtos, ICMS, ST, frete, IPI e totais IBS/CBS.
 */
export const Resumo: React.FC = () => {
  const { produtos, setSubTotais, consideraIbsCbs } = useEntradaNFE();
  const [calculos, setCalculos] = useState<Calculos>(INITIAL_CALCULOS);

  const calculaResumo = useCallback(() => {
    let valTotStItem = 0;
    let valTotProdItem = 0;
    const valTotBonifItem = 0;
    let valTotRetidoItem = 0;
    let valTotStXml = 0;
    let valTotFcpSt = 0;
    let valTotGuiaItem = 0;
    const valTotGuiaItemDif = 0;
    let valTotIpiItem = 0;
    let valTotCredIcmsItem = 0;
    let valTotFrete = 0;
    let valTotNfItem = 0;
    const valTotNfItemPed = 0;
    let valBcIcms = 0;
    const valTotIcmsAntec = 0;

    // Novos acumuladores IBS/CBS
    let valTotBcIbsCbs = 0;
    let valTotCbs = 0;
    let valTotIbsEst = 0;
    let valTotIbsMun = 0;

    const qtdTodal = produtos.length;

    produtos.forEach((produto) => {
      const qtdEmbalagem = produto.qtd_embalagem;
      if (qtdEmbalagem === 0) return;

      const qtdEntrada = produto.qtd_entrada;
      if (qtdEntrada === 0 || produto.val_tabela === 0) return;

      valTotStXml += produto.val_icms_st_xml || 0;

      if (produto.flg_guia) {
        valTotGuiaItem += produto.val_tot_st_guia || 0;
      } else {
        valTotStItem += produto.val_tot_produto || 0;
        valTotRetidoItem += produto.val_tot_st || 0;
        valTotFcpSt += produto.val_tot_st_fcp || 0;
      }

      // valTotIcmsAntec += produto.val_icms_antec

      valBcIcms += produto.val_tot_bc_icms || 0;
      valTotCredIcmsItem += produto.val_tot_icms_op || 0;

      valTotIpiItem += produto.val_tot_ipi || 0;
      valTotFrete += produto.val_tot_frete || 0;
      valTotProdItem += produto.val_tot_produto || 0;

      if (produto.val_tot_nf > 0) {
        valTotNfItem += produto.val_tot_nf || 0;
      }

      if (toNumberOrZero(produto.cod_ibs_cbs) > 0) {
        const valIcms = getValue(produto, 'val_icms') || 0;
        const valFcpIcmsXml = getValue(produto, 'val_fcp_icms_xml') || 0;
        const valPis = getValue(produto, 'val_pis') || 0;
        const valCofins = getValue(produto, 'val_cofins') || 0;

        // ---- Cálculo IBS/CBS por item ----
        const { val_bc_ibs_cbs, val_cbs, val_ibs_est, val_ibs_mun } =
          calculaImpostosIbsCbs(
            {
              per_aliq_cbs: toNumberOrZero(produto.per_aliq_cbs),
              per_aliq_ibs_est: toNumberOrZero(produto.per_aliq_ibs_est),
              per_aliq_ibs_mun: toNumberOrZero(produto.per_aliq_ibs_mun),
              per_red_cbs: toNumberOrZero(produto.per_red_cbs),
              per_red_ibs: toNumberOrZero(produto.per_red_ibs),
              val_red_social: toNumberOrZero(produto.val_red_social),
            },
            toNumberOrZero(produto.val_tabela_liq),
            toNumberOrZero(valIcms) + toNumberOrZero(valFcpIcmsXml),
            toNumberOrZero(valPis) + toNumberOrZero(valCofins),
            toNumberOrZero(produto.cod_ibs_cbs),
          );

        // Acumula totais IBS/CBS (garantindo número)
        valTotBcIbsCbs += toNumberOrZero(val_bc_ibs_cbs);
        valTotCbs += toNumberOrZero(val_cbs);
        valTotIbsEst += toNumberOrZero(val_ibs_est);
        valTotIbsMun += toNumberOrZero(val_ibs_mun);

        if (consideraIbsCbs) {
          produto.val_bc_ibs_cbs = produto.val_bc_ibs_cbs_xml || 0;
          produto.val_cbs = produto.val_cbs_xml || 0;
          produto.val_ibs_mun = produto.val_ibs_mun_xml || 0;
          produto.val_ibs_est = produto.val_ibs_est_xml || 0;
          produto.per_aliq_ibs_mun = produto.per_ibs_mun_xml || 0;
          produto.per_aliq_ibs_est = produto.per_ibs_est_xml || 0;
          produto.per_aliq_cbs = produto.per_cbs_xml || 0;
          produto.per_red_ibs = produto.per_red_ibs_xml || 0;
          produto.per_red_cbs = produto.per_red_cbs_xml || 0;
        }

        produto.val_imp_ibs_cbs = fltRound(
          (toNumberOrZero(produto.val_ibs_mun) +
            toNumberOrZero(produto.val_ibs_est) +
            toNumberOrZero(produto.val_cbs)) /
            qtdTodal,
          4,
        );
        produto.val_custo_sibscbs =
          fltRound(
            toNumberOrZero(produto.val_custo_sibscbs) -
              toNumberOrZero(produto.val_imp_ibs_cbs),
            2,
          ) <= 0
            ? 0
            : fltRound(
                toNumberOrZero(produto.val_custo_sibscbs) -
                  toNumberOrZero(produto.val_imp_ibs_cbs),
                2,
              );
        produto.val_custo_sibscbs_fisc =
          fltRound(
            toNumberOrZero(produto.val_custo_sibscbs_fisc) -
              toNumberOrZero(produto.val_imp_ibs_cbs),
            2,
          ) <= 0
            ? 0
            : fltRound(
                toNumberOrZero(produto.val_custo_sibscbs_fisc) -
                  toNumberOrZero(produto.val_imp_ibs_cbs),
                2,
              );
        produto.val_imp_ibs_cbs = fltRound(
          toNumberOrZero(produto.val_ibs_mun) +
            toNumberOrZero(produto.val_ibs_est) +
            toNumberOrZero(produto.val_cbs),
          4,
        );
      }
    });

    // Arredondamentos padrão
    valTotStItem = parseFloat(valTotStItem.toFixed(2));
    valTotRetidoItem = parseFloat(valTotRetidoItem.toFixed(2));
    valTotStXml = parseFloat(valTotStXml.toFixed(2));
    valTotFcpSt = parseFloat(valTotFcpSt.toFixed(2));
    valTotGuiaItem = parseFloat(valTotGuiaItem.toFixed(2));
    valTotProdItem = parseFloat(valTotProdItem.toFixed(2));
    valBcIcms = parseFloat(valBcIcms.toFixed(2));
    valTotIpiItem = parseFloat(valTotIpiItem.toFixed(2));
    valTotCredIcmsItem = parseFloat(valTotCredIcmsItem.toFixed(2));
    valTotFrete = parseFloat(valTotFrete.toFixed(2));
    valTotNfItem = parseFloat(valTotNfItem.toFixed(2));

    // Arredondamentos IBS/CBS
    valTotBcIbsCbs = parseFloat(valTotBcIbsCbs.toFixed(2));
    valTotCbs = parseFloat(valTotCbs.toFixed(2));
    valTotIbsEst = parseFloat(valTotIbsEst.toFixed(2));
    valTotIbsMun = parseFloat(valTotIbsMun.toFixed(2));

    // Regras específicas de arredondamento
    valTotProdItem = roundByRule(valTotProdItem);
    valTotNfItem = roundByRule(valTotNfItem);

    const valTotIva = valTotGuiaItem + valTotGuiaItemDif;

    const novosCalculos: Calculos = {
      valTotProdItem,
      valBcIcms,
      valTotCredIcmsItem,
      valTotFrete,
      valTotRetidoItem,
      valTotStXml,
      valTotFcpSt,
      valTotIva,
      valTotIpiItem,
      valTotIcmsAntec,
      valTotNfItem,
      valTotNfItemPed,
      valTotBcIbsCbs,
      valTotCbs,
      valTotIbsEst,
      valTotIbsMun,
    };

    setSubTotais(novosCalculos);
    setCalculos(novosCalculos);
  }, [produtos, setSubTotais]);

  useEffect(() => {
    calculaResumo();
  }, [calculaResumo]);

  return (
    <Container>
      {/* 1ª linha */}
      <Row>
        <Col sm={12} md={3}>
          <strong>Total dos Produtos:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotProdItem)}
        </Col>
        <Col sm={12} md={3}>
          <strong>ST (NF):</strong>{' '}
          {formatCurrencyAsText(calculos.valTotRetidoItem)}
        </Col>
        <Col sm={12} md={3}>
          <strong>Frete (NF):</strong>{' '}
          {formatCurrencyAsText(calculos.valTotFrete)}
        </Col>
        <Col sm={12} md={3}>
          <strong>BC IBS/CBS:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotBcIbsCbs)}
        </Col>
      </Row>

      {/* 2ª linha */}
      <Row className="mt-2">
        <Col sm={12} md={3}>
          <strong>BC ICMS OP:</strong>{' '}
          {formatCurrencyAsText(calculos.valBcIcms)}
        </Col>
        <Col sm={12} md={3}>
          <strong>ST (XML):</strong>{' '}
          {formatCurrencyAsText(calculos.valTotStXml)}
        </Col>
        <Col sm={12} md={3}>
          <strong>(Guia) ST:</strong> {formatCurrencyAsText(calculos.valTotIva)}
        </Col>
        <Col sm={12} md={3}>
          <strong>CBS:</strong> {formatCurrencyAsText(calculos.valTotCbs)}
        </Col>
      </Row>

      {/* 3ª linha */}
      <Row className="mt-2">
        <Col sm={12} md={3}>
          <strong>ICMS OP:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotCredIcmsItem)}
        </Col>
        <Col sm={12} md={3}>
          <strong>FCP ST:</strong> {formatCurrencyAsText(calculos.valTotFcpSt)}
        </Col>
        <Col sm={12} md={3}>
          <strong>Val. IPI:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotIpiItem)}
        </Col>
        <Col sm={12} md={3}>
          <strong>IBS Est.:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotIbsEst)}
        </Col>
      </Row>

      {/* 4ª linha – IBS Municipal embaixo do IBS Estadual (mesma coluna) */}
      <Row className="mt-2">
        <Col sm={12} md={3}>
          <strong>Total da NF:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotNfItem)}
        </Col>
        <Col sm={12} md={3} />
        <Col sm={12} md={3} />
        <Col sm={12} md={3}>
          <strong>IBS Mun.:</strong>{' '}
          {formatCurrencyAsText(calculos.valTotIbsMun)}
        </Col>
      </Row>
    </Container>
  );
};
